import React, { useState } from 'react';
import { formatPeso } from '../utils/formatPrice';
import { stockSummary } from '../utils/stockAvailability';

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E';

const toneClass = {
  ok: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200',
  low: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200',
  out: 'bg-red-50 text-red-800 ring-1 ring-red-200',
  neutral: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
};

export default function ProductCard({ product, backendUrl, onAddToCart, onShowDetails, storeName }) {
  const imageSrc = product.image?.startsWith('http')
    ? product.image
    : product.image ? `${backendUrl}${product.image}` : PLACEHOLDER_IMAGE;
  const [imgSrc, setImgSrc] = useState(imageSrc);

  const handleImageError = () => setImgSrc(PLACEHOLDER_IMAGE);

  const openDetails = () => onShowDetails?.(product._id);
  const stock = stockSummary(product, { storeName });

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:border-slate-300 hover:shadow-md">
      <button
        type="button"
        className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
        onClick={openDetails}
        aria-label={`View details for ${product.name}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
          <img
            src={imgSrc}
            alt={product.name || 'Product'}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        </div>
      </button>
      <div className="flex flex-1 flex-col p-4 pt-3">
        <button
          type="button"
          className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
          onClick={openDetails}
          aria-label={`View details for ${product.name}`}
        >
          <h3
            className="min-h-[2.5rem] text-sm font-semibold leading-snug text-slate-900 sm:text-base group-hover:text-amber-700"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {product.name}
          </h3>
        </button>
        {product.category ? (
          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-400">
            {product.category}
          </p>
        ) : null}
        <p className="mt-2 text-lg font-bold tabular-nums text-slate-900 sm:text-xl">
          {formatPeso(product.price)}
        </p>
        <p
          className={`mt-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold leading-snug ${toneClass[stock.tone] || toneClass.neutral}`}
        >
          {stock.text}
        </p>
        <div className="mt-auto flex gap-2 pt-4">
          <button
            type="button"
            className="flex-1 min-h-[44px] sm:min-h-0 rounded-lg bg-amber-500 py-2.5 text-sm font-semibold text-slate-900 shadow-sm touch-manipulation hover:bg-amber-400 sm:py-2"
            onClick={() => onAddToCart(product)}
            aria-label={`Add ${product.name} to cart`}
          >
            Add to cart
          </button>
          <button
            type="button"
            className="flex-1 min-h-[44px] sm:min-h-0 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-700 touch-manipulation hover:bg-slate-50 sm:py-2"
            onClick={openDetails}
            aria-label={`View details for ${product.name}`}
          >
            Details
          </button>
        </div>
      </div>
    </article>
  );
}
